/**
 * PricingSection — admin-only Phyllo pricing card.
 *
 * Renders the populated subset of doc.pricing.post_type as a 2-col card
 * grid (currency at top, final_calculated_price as footer summary).
 * applied_multipliers shown as a pill row below; "Show all" toggle
 * reveals null multipliers (dim) when any exist.
 *
 * Gated admin-only at the call site in AnalyticsTab.
 */
import { useMemo, useState } from 'react';
import type {
  InsightIQPricing,
  InsightIQPostTypeEntry,
  InsightIQPricingMultipliers,
} from '@discovr/types';
import { SectionTitle } from '../charts/StatCard';
import { fmtNum, fmtDecimal2 } from '../utils/formatters';

interface PricingSectionProps {
  pricing: InsightIQPricing;
}

// Wire-key (with hyphens) → display label. Mirrors BE model.go field order.
const POST_TYPE_LABELS: ReadonlyArray<readonly [keyof NonNullable<InsightIQPricing['post_type']>, string]> = [
  ['reels', 'Reels'],
  ['story', 'Story'],
  ['static_post', 'Static Post'],
  ['carousel', 'Carousel'],
  ['video', 'Video'],
  ['shorts', 'Shorts'],
  ['live_stream', 'Live Stream'],
  ['community_post-text_post', 'Community · Text'],
  ['community_post-image_post', 'Community · Image'],
  ['community_post-poll', 'Community · Poll'],
  ['community_post-video_share', 'Community · Video Share'],
  ['photo_post', 'Photo Post'],
  ['text_post', 'Text Post'],
] as const;

const MULTIPLIER_META: ReadonlyArray<readonly [keyof InsightIQPricingMultipliers, string, 'currency' | 'multiplier']> = [
  ['base_rate', 'Base rate', 'currency'],
  ['base_cpm', 'Base CPM', 'currency'],
  ['views_multiplier', 'Views', 'multiplier'],
  ['engagement_multiplier', 'Engagement', 'multiplier'],
  ['category_multiplier', 'Category', 'multiplier'],
  ['content_type_multiplier', 'Content type', 'multiplier'],
  ['audience_location_multiplier', 'Audience location', 'multiplier'],
  ['shop_multiplier', 'Shop', 'multiplier'],
] as const;

export function PricingSection({ pricing }: PricingSectionProps) {
  const currency = pricing.currency || 'USD';

  const populatedPostTypes = useMemo(() => {
    const pt = pricing.post_type ?? {};
    return POST_TYPE_LABELS
      .map(([key, label]) => {
        const entry = pt[key];
        return entry ? ([label, entry] as const) : null;
      })
      .filter((x): x is readonly [string, InsightIQPostTypeEntry] => x !== null);
  }, [pricing.post_type]);

  const multipliers = pricing.applied_multipliers;
  const hasMultipliers = multipliers != null;
  const hasNullMultipliers = useMemo(() => {
    if (!multipliers) return false;
    return MULTIPLIER_META.some(([key]) => multipliers[key] == null);
  }, [multipliers]);

  const [showAllMultipliers, setShowAllMultipliers] = useState(false);

  return (
    <div className="rounded-2xl border border-border bg-card p-5 mb-4">
      <SectionTitle>Pricing</SectionTitle>
      <div className="mb-3 text-caption-medium uppercase tracking-[0.06em] text-muted-foreground">
        {currency} · per post
      </div>

      {populatedPostTypes.length > 0 && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {populatedPostTypes.map(([label, entry]) => (
            <PostTypeCard
              key={label}
              label={label}
              currency={currency}
              range={entry.price_range}
            />
          ))}
        </div>
      )}

      {pricing.final_calculated_price != null && (
        <div className="mt-3 flex items-baseline justify-between border-t border-border pt-3">
          <div className="text-caption-medium uppercase tracking-[0.06em] text-muted-foreground">
            Final reference · {currency}
          </div>
          <div className="text-heading-h7 text-foreground">
            {fmtNum(pricing.final_calculated_price)}
          </div>
        </div>
      )}

      {hasMultipliers && (
        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-body-sm-semibold uppercase tracking-wider text-muted-foreground">
              Multipliers
            </div>
            {hasNullMultipliers && (
              <button
                type="button"
                onClick={() => setShowAllMultipliers((v) => !v)}
                className="text-[12px] font-semibold text-muted-foreground hover:text-foreground"
              >
                {showAllMultipliers ? 'Show fewer' : 'Show all'}
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {MULTIPLIER_META.map(([key, label, kind]) => {
              const value = multipliers?.[key];
              const isNull = value == null;
              if (isNull && !showAllMultipliers) return null;
              return (
                <MultiplierPill
                  key={key}
                  name={label}
                  value={value ?? null}
                  kind={kind}
                  currency={currency}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function PostTypeCard({
  label,
  currency,
  range,
}: {
  label: string;
  currency: string;
  range: { min: number; max: number };
}) {
  return (
    <div className="rounded-lg border border-border px-3 py-2">
      <div className="text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-body-md-medium tabular-nums text-foreground">
        {currency} {fmtNum(range.min)} – {fmtNum(range.max)}
      </div>
    </div>
  );
}

function MultiplierPill({
  name,
  value,
  kind,
  currency,
}: {
  name: string;
  value: number | null;
  kind: 'currency' | 'multiplier';
  currency: string;
}) {
  const isNull = value == null;
  const isNonNeutral = !isNull && kind === 'multiplier' && Math.abs(value - 1) > 0.001;

  let tone = 'bg-muted text-foreground';
  if (isNull) tone = 'bg-muted text-muted-foreground border border-dashed border-border';
  else if (isNonNeutral) tone = 'bg-yellow-50 text-yellow-700';

  const display = isNull
    ? '—'
    : kind === 'currency'
    ? `${currency} ${fmtNum(value)}`
    // fmtDecimal2 falls back to the same dash as a missing multiplier when
    // the value is exactly 0 — a 0× multiplier reads as "no data" here too.
    : `${fmtDecimal2(value) ?? '—'}×`;

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold ${tone}`}>
      <span className="text-[10px] font-medium uppercase tracking-wider opacity-80">{name}</span>
      <span className="tabular-nums">{display}</span>
    </span>
  );
}
